/** Client mirror of server transport source constants. */

import {
  transportOperationalGroupLabel,
  transportEnablementStatusLabel,
  transportReadinessTierLabel,
} from "./transportSourceEnablement";

export const TRANSPORT_SOURCE_TYPES = Object.freeze({
  OFFICIAL: "official",
  COMMUNITY: "community",
  THIRD_PARTY: "third_party",
  DERIVED: "derived",
  INTERNAL_MOCK: "internal_mock",
});

export const TRANSPORT_MODES = Object.freeze({
  METRO: "metro",
  BUS: "bus",
  TRAM: "tram",
  CABLE: "cable",
  BRT: "brt",
  RAIL: "rail",
  ROAD: "road",
  PEDESTRIAN: "pedestrian",
  BICYCLE: "bicycle",
  MULTIMODAL: "multimodal",
  UNKNOWN: "unknown",
});

export const TRANSPORT_FEATURE_TYPES = Object.freeze({
  METRO_STATION: "metro_station",
  METRO_LINE: "metro_line",
  BUS_STOP: "bus_stop",
  BUS_ROUTE: "bus_route",
  ROAD_CORRIDOR: "road_corridor",
  TRANSPORT_INCIDENT: "transport_incident",
  WEATHER_DISRUPTION_SIGNAL: "weather_disruption_signal",
  COMMUNITY_MOBILITY_REPORT: "community_mobility_report",
  ACCESS_SCORE: "access_score",
});

export const TRANSPORT_HEALTH_STATUSES = Object.freeze({
  HEALTHY: "healthy",
  STALE: "stale",
  DEGRADED: "degraded",
  FAILING: "failing",
  NOT_CONFIGURED: "not_configured",
  DISCOVERY_PENDING: "discovery_pending",
  DISABLED: "disabled",
  STUB_ONLY: "stub_only",
});

export const TRANSPORT_SOURCE_KEYS = Object.freeze({
  METRO_MEDELLIN_GTFS: "METRO_MEDELLIN_GTFS",
  METRO_MEDELLIN_AFLUENCIA: "METRO_MEDELLIN_AFLUENCIA",
  GEOMEDELLIN_TRANSPORT_STOPS: "GEOMEDELLIN_TRANSPORT_STOPS",
  GEOMEDELLIN_TRANSPORT_ROUTES: "GEOMEDELLIN_TRANSPORT_ROUTES",
  OSM_TRANSPORT: "OSM_TRANSPORT",
  TRANSPORT_WEATHER_DISRUPTION_SIGNALS: "TRANSPORT_WEATHER_DISRUPTION_SIGNALS",
  COMMUNITY_MOBILITY_REPORTS: "COMMUNITY_MOBILITY_REPORTS",
  TRANSPORT_ACCESS_SCORING: "TRANSPORT_ACCESS_SCORING",
  OSM_ROAD_NETWORK: "OSM_ROAD_NETWORK",
  MANUAL_ROAD_INCIDENTS: "MANUAL_ROAD_INCIDENTS",
  COMMUNITY_ROAD_REPORTS: "COMMUNITY_ROAD_REPORTS",
  ANTIOQUIA_ROAD_NETWORK: "ANTIOQUIA_ROAD_NETWORK",
  INVIAS_ROAD_STATUS: "INVIAS_ROAD_STATUS",
  ANI_CONCESSION_ROUTES: "ANI_CONCESSION_ROUTES",
  METRO_MEDELLIN_SERVICE_ALERTS: "METRO_MEDELLIN_SERVICE_ALERTS",
  METRO_MEDELLIN_SERVICE_NOTICES: "METRO_MEDELLIN_SERVICE_NOTICES",
  MEDELLIN_MOVILITY_ALERTS: "MEDELLIN_MOVILITY_ALERTS",
  MANUAL_OFFICIAL_TRANSPORT_NOTICES: "MANUAL_OFFICIAL_TRANSPORT_NOTICES",
  THIRD_PARTY_TRANSPORT_STATUS_CANDIDATE: "THIRD_PARTY_TRANSPORT_STATUS_CANDIDATE",
  METRO_MEDELLIN_GTFS_RT_TRIP_UPDATES: "METRO_MEDELLIN_GTFS_RT_TRIP_UPDATES",
  METRO_MEDELLIN_GTFS_RT_VEHICLE_POSITIONS: "METRO_MEDELLIN_GTFS_RT_VEHICLE_POSITIONS",
  METRO_MEDELLIN_GTFS_RT_SERVICE_ALERTS: "METRO_MEDELLIN_GTFS_RT_SERVICE_ALERTS",
});

export const TRANSPORT_SEEDED_SOURCE_KEYS = Object.freeze(
  Object.values(TRANSPORT_SOURCE_KEYS)
);

export function isTransportDataHealthSource(sourceCode) {
  const normalized = String(sourceCode || "").trim().toUpperCase();
  return TRANSPORT_SEEDED_SOURCE_KEYS.includes(normalized);
}

export const TRANSPORT_SOURCE_GROUP_LABELS = {
  transport_registry: "Transport registry",
};

export function transportSourceGroupLabel(sourceGroup) {
  return (
    transportOperationalGroupLabel(sourceGroup) ||
    TRANSPORT_SOURCE_GROUP_LABELS[sourceGroup] ||
    null
  );
}

export function transportSourceDetailLines(source) {
  const metadata = source?.transportMetadata;
  if (!metadata) {
    return [];
  }

  const lines = [];
  if (metadata.accessMethod) {
    lines.push(`Access: ${metadata.accessMethod}`);
  }
  if (metadata.reviewStatus) {
    lines.push(`Review: ${metadata.reviewStatus}`);
  }
  if (metadata.confidenceLevel) {
    lines.push(`Confidence: ${metadata.confidenceLevel}`);
  }
  if (metadata.mapFeaturesCount != null) {
    lines.push(`Map features: ${metadata.mapFeaturesCount}`);
  }
  if (metadata.routesCount != null) {
    lines.push(`Routes: ${metadata.routesCount}`);
  }
  if (metadata.stopsCount != null) {
    lines.push(`Stops: ${metadata.stopsCount}`);
  }
  if (metadata.shapesCount != null) {
    lines.push(`Shapes: ${metadata.shapesCount}`);
  }
  if (metadata.mobilityReportsTotal != null) {
    lines.push(`Mobility reports: ${metadata.mobilityReportsTotal}`);
  }
  if (metadata.mobilityReportsOpen != null) {
    lines.push(`Open reports: ${metadata.mobilityReportsOpen}`);
  }
  if (metadata.mobilityReportsAwaitingReview != null) {
    lines.push(`Awaiting review: ${metadata.mobilityReportsAwaitingReview}`);
  }
  if (metadata.accessScoresTotal != null) {
    lines.push(`Access scores: ${metadata.accessScoresTotal}`);
  }
  if (metadata.accessScoresAverage != null) {
    lines.push(`Avg access score: ${metadata.accessScoresAverage}`);
  }
  if (metadata.accessScoresMethodVersion) {
    lines.push(`Method: ${metadata.accessScoresMethodVersion}`);
  }
  if (metadata.configured === false || metadata.configuredFlag === false) {
    lines.push("URL/path not configured");
  }
  if (metadata.enablementStatus) {
    lines.push(
      `Enablement: ${metadata.enablementStatusLabel || transportEnablementStatusLabel(metadata.enablementStatus)}`
    );
  }
  if (metadata.readinessTier) {
    lines.push(`Tier: ${transportReadinessTierLabel(metadata.readinessTier) || metadata.readinessTier}`);
  }
  if (metadata.recommendedNextAction) {
    lines.push(`Next: ${metadata.recommendedNextAction}`);
  }
  if (metadata.latestRunMessage) {
    lines.push(metadata.latestRunMessage);
  }
  return lines;
}
